import { useField, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import InputError from '~/components/feedback/InputError';
import type { IDateOfBirthInput } from '~/types/inputs';

function RawInput({
	name,
	label,
	onChange,
	value,
	placeholder,
	...rest
}: {
	value: string;
	name: string;
	label: string;
	onChange: (newValue: string) => void;
	placeholder: string;
	[key: string]: any;
}) {
	const handleChange = event => {
		const newValue = event.target.value;

		onChange(newValue);
	};

	return (
		<div className="simple-input-container rounded-tl-input rounded-tr-input relative h-input w-full">
			<input
				{...rest}
				type="number"
				onChange={handleChange}
				className="simple-input-input peer bg-transparent placeholder:font-body-large placeholder:currentColor w-full outline-none pl-5 pr-5 pt-6 pb-1 h-full"
				id={name}
				name={name}
				placeholder={placeholder}
			/>
			<label
				htmlFor={name}
				className="simple-input-label pointer-events-none transition-all top-3 text-xs absolute left-5 font-large-body"
			>
				{label}
			</label>
			<div className="simple-input-border absolute -bottom-[2px] left-0 w-full h-[2px]" />
		</div>
	);
}

export default function DateOfBirth({ name, label, required }: IDateOfBirthInput) {
	const [_, meta, helpers] = useField(name);
	const { submitCount } = useFormikContext();
	const isError = (meta.touched || submitCount > 0) && !!meta.error;
	const [day, setDay] = useState('');
	const [month, setMonth] = useState('');
	const [year, setYear] = useState('');

	useEffect(() => {
		if (!day || !month || !year) {
			helpers.setValue('');
			return;
		}

		helpers.setValue(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
	}, [day, month, year]);

	return (
		<>
			<fieldset className="relative col-span-6 text-left">
				<legend className="pl-5 mb-2">
					{label}
					{required && !label.endsWith('*') ? <sup>*</sup> : null}
				</legend>
				<div className="flex gap-4">
					<RawInput
						onChange={setDay}
						value={day}
						max={31}
						min={1}
						name={`${name}.day`}
						label="Day"
						placeholder="DD"
					/>
					<RawInput
						onChange={setMonth}
						value={month}
						max={12}
						min={1}
						name={`${name}.month`}
						label="Month"
						placeholder="MM"
					/>
					<RawInput
						onChange={setYear}
						value={year}
						max={new Date().getFullYear()}
						min={1900}
						name={`${name}.year`}
						label="Year"
						placeholder="YYYY"
					/>
				</div>
				<InputError name={name} isError={isError}>
					{meta.error}
				</InputError>
			</fieldset>
		</>
	);
}
